import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -----------------------------------------------------------------------------
// Token storage strategy — deliberate choice
// -----------------------------------------------------------------------------
// We store the JWT in **sessionStorage** (not localStorage, not an HTTP-only cookie).
//
// Why sessionStorage?
//   - Cleared automatically when the browser tab closes → shorter attack window.
//   - Same-origin only, so an unrelated tab can't read it.
//   - No CSRF surface: because we send the token via the `Authorization` header,
//     nothing is auto-attached to cross-site requests the way a cookie would be.
//
// Why not HTTP-only cookies?
//   - Would require moving to cookie-based sessions and a CSRF token layer;
//     that's a full auth rewrite for a marginal security win in this app's
//     threat model (single-tenant, teacher-facing curriculum tool).
//
// XSS is the real remaining risk. Mitigations we already rely on:
//   - React auto-escapes all rendered strings.
//   - We never call `dangerouslySetInnerHTML` on user content.
//   - CSP + Content-Type headers on the backend.
// -----------------------------------------------------------------------------
//
// Apply or remove the global Authorization header so every axios call carries
// the JWT without each component needing to pass it manually.
const applyAxiosAuthHeader = (tkn) => {
  if (tkn) {
    axios.defaults.headers.common.Authorization = `Bearer ${tkn}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
};

// Rehydrate on module load (before <AuthProvider/> mounts) so a hard-refresh
// keeps the user authenticated for any data fetches that fire on first render.
applyAxiosAuthHeader(sessionStorage.getItem('_t'));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const tokenRef = useRef(sessionStorage.getItem('_t'));
  const [token, setTokenState] = useState(tokenRef.current);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState({ tier: 'free', status: 'inactive' });

  const persistToken = useCallback((newToken) => {
    tokenRef.current = newToken;
    setTokenState(newToken);
    applyAxiosAuthHeader(newToken);
    if (newToken) {
      sessionStorage.setItem('_t', newToken);
    } else {
      sessionStorage.removeItem('_t');
    }
  }, []);

  // "Continue as" memory — persists across sessions so returning users can 1-click resume.
  // Stored in localStorage (not sessionStorage) so it survives tab close.
  const rememberLastLogin = useCallback((u, method) => {
    if (!u || !u.email) return;
    try {
      localStorage.setItem('last_login', JSON.stringify({
        name: u.name || '',
        email: u.email,
        picture: u.picture || '',
        method, // 'google' | 'password'
        at: Date.now(),
      }));
  } catch (err) { console.warn('AuthContext: localStorage write failed', err); }
  }, []);

  const forgetLastLogin = useCallback(() => {
    try { localStorage.removeItem('last_login'); } catch (err) { console.warn('AuthContext: localStorage clear failed', err); }
  }, []);

  const fetchSubscription = useCallback(async (tkn) => {
    try {
      const res = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${tkn}` }
      });
      setSubscription(res.data);
    } catch (err) {
      console.warn('AuthContext: subscription fetch failed, falling back to free', err);
      setSubscription({ tier: 'free', status: 'inactive' });
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setUser(res.data);
        fetchSubscription(token);
      }).catch(() => {
        persistToken(null);
        setUser(null);
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, persistToken, fetchSubscription]);

  const refreshSubscription = useCallback(() => {
    if (token) fetchSubscription(token);
  }, [token, fetchSubscription]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    persistToken(res.data.token);
    setUser(res.data.user);
    rememberLastLogin(res.data.user, 'password');
    fetchSubscription(res.data.token);
    return res.data.user;
  };

  const register = async (name, email, password, role, gradeLevel) => {
    const res = await axios.post(`${API}/auth/register`, {
      name, email, password, role, grade_level: gradeLevel
    });
    persistToken(res.data.token);
    setUser(res.data.user);
    rememberLastLogin(res.data.user, 'password');
    return res.data.user;
  };

  const googleAuth = async (code, redirectUri) => {
    const res = await axios.post(`${API}/auth/google`, { code, redirect_uri: redirectUri });
    persistToken(res.data.token);
    setUser(res.data.user);
    rememberLastLogin(res.data.user, 'google');
    return res.data.user;
  };

  const logout = () => {
    persistToken(null);
    setUser(null);
    setSubscription({ tier: 'free', status: 'inactive' });
    // Note: We DO NOT clear last_login here — keep the "Continue as" affordance
    // so the same user can quickly sign in again. Use forgetLastLogin() explicitly
    // (via the "Not you?" link) to clear it.
  };

  const isApproved = user && user.status === 'approved';
  const isPending = user && user.status === 'pending';
  const isAdmin = user && user.role === 'admin';
  const isTeacherOrAdmin = user && (user.role === 'teacher' || user.role === 'admin');
  const hasTier = useCallback((tier) => {
    if (isAdmin) return true;
    if (tier === 'basic') return subscription.tier === 'basic' || subscription.tier === 'pro';
    if (tier === 'pro') return subscription.tier === 'pro';
    return false;
  }, [isAdmin, subscription.tier]);

  // Memoize the context value so consumers don't re-render on every parent render.
  const contextValue = useMemo(() => ({
    user, token, loading, login, register, googleAuth, logout,
    isApproved, isPending, isAdmin, isTeacherOrAdmin,
    subscription, hasTier, refreshSubscription,
    forgetLastLogin,
  }), [
    user, token, loading, login, register, googleAuth, logout,
    isApproved, isPending, isAdmin, isTeacherOrAdmin,
    subscription, hasTier, refreshSubscription, forgetLastLogin,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
